import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { ArticleAny } from "@/types"
import useCopyToClipboard from "components/CopyToClipboard/useCopyToClipboard"
import Asset from "models/Asset"
import { useTenantSettings } from "stores/tenantSettings"
import useAssetCollection from "hooks/useAssetCollection"
import { getTranslation } from "utils/translations"

export default function useCopyFeatures({
    asset,
    article,
}: {
    asset: Asset
    article: ArticleAny
}) {
    const { t } = useTranslation()
    const { tenantPrimaryLanguageIsoCode } = useTenantSettings()
    const { fetchAssetCollection } = useAssetCollection(asset.linkGetAssetCollection)
    const copyToClipboard = useCopyToClipboard()

    const onCopyFeatures = useCallback(async (features: string) => {
        const assetCollection = await fetchAssetCollection()

        let text = `${asset.lmid} ${asset.altArticleDescription || article?.description || ""}`
        if (features) {
            text += `\n${features}`
        }

        if (assetCollection.assets.length) {
            text += `\n\n${t('asset.connectedAssets')}:`
            for (const connectedAsset of assetCollection.assets) {
                text += `\n\n${connectedAsset.lmid} ${connectedAsset.altArticleDescription || connectedAsset.articleDescription || ""}`
                const connectedAssetFeatures = getTranslation(connectedAsset.features, tenantPrimaryLanguageIsoCode)
                if (connectedAssetFeatures) {
                    text += `\n${connectedAssetFeatures}`
                }
            }
        }

        copyToClipboard({
            text, toast: {
                title: t('asset.copyFeaturesToastTitle'),
                descr: t('asset.copyFeaturesToastDescr'),
            }
        })
    }, [article?.description, asset.altArticleDescription, asset.lmid, copyToClipboard, fetchAssetCollection, t,
        tenantPrimaryLanguageIsoCode])

    return { onCopyFeatures }
}